import type { LevelData } from '@/game/level';

export interface PackMeta {
  name: string;
  author: string;
  description?: string;
  createdAt?: string;
}

export interface Pack {
  format: 'tdash-pack';
  version: 1;
  meta: PackMeta;
  levels: LevelData[];
}

const CURRENT_PACK_VERSION = 1;

export function serializePack(meta: Omit<PackMeta, 'createdAt'>, levels: LevelData[]): string {
  const p: Pack = {
    format: 'tdash-pack',
    version: CURRENT_PACK_VERSION,
    meta: { ...meta, createdAt: new Date().toISOString() },
    levels,
  };
  return JSON.stringify(p, null, 2);
}

export type ImportResult =
  | { kind: 'ok'; levels: LevelData[]; packMeta?: PackMeta }
  | { kind: 'error'; message: string }
  | { kind: 'version-too-new' };

export function parseImport(json: string): ImportResult {
  let data: unknown;
  try {
    data = JSON.parse(json);
  } catch (e) {
    return { kind: 'error', message: 'File is not valid JSON' };
  }
  if (typeof data !== 'object' || data === null) {
    return { kind: 'error', message: 'Expected a JSON object' };
  }
  const rec = data as Record<string, unknown>;
  if (rec.format === 'tdash-pack') {
    const v = rec.version as number | undefined;
    if (typeof v !== 'number') return { kind: 'error', message: 'Pack is missing a version' };
    if (v > CURRENT_PACK_VERSION) return { kind: 'version-too-new' };
    if (!Array.isArray(rec.levels)) return { kind: 'error', message: 'Pack has no levels' };
    return {
      kind: 'ok',
      levels: rec.levels as LevelData[],
      packMeta: rec.meta as PackMeta,
    };
  }
  if (rec.meta && (rec.meta as Record<string, unknown>).id) {
    return { kind: 'ok', levels: [rec as unknown as LevelData] };
  }
  return { kind: 'error', message: 'File does not look like a TriggernometryDash level or pack' };
}

export type Resolution = 'replace' | 'keep-both' | 'skip';

export interface ConflictEntry {
  incoming: LevelData;
  conflict: boolean;
  resolution?: Resolution;
}

export function planConflicts(incoming: LevelData[], existing: LevelData[]): ConflictEntry[] {
  const byId = new Set(existing.map((l) => l.meta.id));
  return incoming.map((l) => ({ incoming: l, conflict: byId.has(l.meta.id) }));
}

/** Returns the resulting userLevels list after applying the plan. */
export function applyConflictPlan(plan: ConflictEntry[], existing: LevelData[]): LevelData[] {
  const out = existing.slice();
  for (const e of plan) {
    const res = e.resolution ?? (e.conflict ? 'skip' : 'replace');
    if (!e.conflict || res === 'replace') {
      const idx = out.findIndex((l) => l.meta.id === e.incoming.meta.id);
      if (idx >= 0) out[idx] = e.incoming;
      else out.unshift(e.incoming);
    } else if (res === 'keep-both') {
      const copy = JSON.parse(JSON.stringify(e.incoming)) as LevelData;
      copy.meta.id = crypto.randomUUID();
      copy.meta.name = `${copy.meta.name} (imported)`;
      out.push(copy);
    }
  }
  return out;
}
